/**
 * Mock data for the Style Guide page.
 * Provides sample UI config structures for demonstrating container types,
 * components, attributes, and spacing tokens.
 */

import badgeScannerImg from './assets/badge-scanner.png';

// ============================================================================
// Placeholder Images (SVG Data URLs)
// ============================================================================

/**
 * Colored square placeholder images (80x80) for use in cards and products.
 * Using inline SVG data URLs for zero external dependencies.
 */
export const PLACEHOLDER_IMAGES = {
  red: `data:image/svg+xml,${encodeURIComponent(
    '<svg xmlns="http://www.w3.org/2000/svg" width="80" height="80"><rect fill="#E53935" width="80" height="80"/></svg>',
  )}`,
  blue: `data:image/svg+xml,${encodeURIComponent(
    '<svg xmlns="http://www.w3.org/2000/svg" width="80" height="80"><rect fill="#1E88E5" width="80" height="80"/></svg>',
  )}`,
  green: `data:image/svg+xml,${encodeURIComponent(
    '<svg xmlns="http://www.w3.org/2000/svg" width="80" height="80"><rect fill="#43A047" width="80" height="80"/></svg>',
  )}`,
  yellow: `data:image/svg+xml,${encodeURIComponent(
    '<svg xmlns="http://www.w3.org/2000/svg" width="80" height="80"><rect fill="#FDD835" width="80" height="80"/></svg>',
  )}`,
  purple: `data:image/svg+xml,${encodeURIComponent(
    '<svg xmlns="http://www.w3.org/2000/svg" width="80" height="80"><rect fill="#8E24AA" width="80" height="80"/></svg>',
  )}`,
  gray: `data:image/svg+xml,${encodeURIComponent(
    '<svg xmlns="http://www.w3.org/2000/svg" width="80" height="80"><rect fill="#757575" width="80" height="80"/></svg>',
  )}`,
  orange: `data:image/svg+xml,${encodeURIComponent(
    '<svg xmlns="http://www.w3.org/2000/svg" width="80" height="80"><rect fill="#FF9800" width="80" height="80"/></svg>',
  )}`,
  teal: `data:image/svg+xml,${encodeURIComponent(
    '<svg xmlns="http://www.w3.org/2000/svg" width="80" height="80"><rect fill="#009688" width="80" height="80"/></svg>',
  )}`,
};

// Product image from Figma design (badge scanner device photo)
export const PRODUCT_IMAGES = {
  badgeScanner: badgeScannerImg,
};

import {
  UICfgContainerType,
  IUICfgRootContainer,
  IUICfgMixedContainer,
  IUICfgComponentItemsContainer,
  IUiCfgContainerExpandable,
  IUICfgProductsMergeContainer,
  IUICfgComponent,
  IUICfgAttributeText,
  UICfgComponentContainerElementOptionsDefault,
  UICfgAttributesContainerElementOptionsDefault,
  UICfgProductsContainerElementOptionsDefault,
} from '@canvas-logic/clp-api-types';
import {
  MockTextAttributeConfig,
  MockBooleanAttributeConfig,
  MockIntegerAttributeConfig,
} from './mocks/MockAttributeStates';
import { MockCompositeProductInfoConfig, MockComponentForMergeConfig } from './mocks/MockComponentStates';
import { MockComponentConfig } from './mocks/MockContainerChildren';
import { IUICfgUICompositeProductInfoElementUI } from '@canvas-logic/clp-api-types';

// ============================================================================
// Mock Container Configurations
// ============================================================================

/**
 * Root container - the top-level container that holds all other UI elements.
 * It serves as the entry point for the configurator UI hierarchy.
 */
export const mockRootContainer: IUICfgRootContainer = {
  id: 'mock-root',
  kind: 'container',
  type: UICfgContainerType.Root,
  name: 'Root Container',
  enabled: true,
  componentsSettings: UICfgComponentContainerElementOptionsDefault,
  attributesSettings: UICfgAttributesContainerElementOptionsDefault,
  children: [],
};

/**
 * Mixed container - a flexible container that can hold components, attributes,
 * products, and nested containers. Supports expandable/collapsible behavior.
 */
export const mockMixedContainer: IUICfgMixedContainer = {
  id: 'mock-mixed-1',
  kind: 'container',
  type: UICfgContainerType.Mixed,
  name: 'Mixed Container',
  enabled: true,
  title: 'Container Title (CnT)',
  description: 'Container description text - explains the purpose of this section (CnD)',
  expandable: true,
  defaultExpanded: true,
  showTitle: true,
  showDescription: true,
  combineIntoGrid: false,
  componentsSettings: UICfgComponentContainerElementOptionsDefault,
  attributesSettings: UICfgAttributesContainerElementOptionsDefault,
  children: [],
};

/**
 * Mixed container configured for grid layout - demonstrates how items
 * can be displayed in a grid format when combineIntoGrid is enabled.
 */
export const mockMixedContainerGrid: IUICfgMixedContainer = {
  id: 'mock-mixed-grid',
  kind: 'container',
  type: UICfgContainerType.Mixed,
  name: 'Mixed Container (Grid)',
  enabled: true,
  title: 'Container Title - Grid Layout (CnT)',
  description: 'Container description - items arranged in grid mode (CnD)',
  expandable: true,
  defaultExpanded: true,
  showTitle: true,
  showDescription: true,
  combineIntoGrid: true,
  componentsSettings: UICfgComponentContainerElementOptionsDefault,
  attributesSettings: UICfgAttributesContainerElementOptionsDefault,
  children: [],
};

/**
 * Component items container - specialized container that groups a component
 * with its related attributes. First child must be a component.
 */
// The Component Items container type does not mix in `IUiCfgContainerExpandable`, but this
// fixture supplies those fields on purpose so the style guide exercises title/expand rendering.
export const mockComponentItemsContainer: IUICfgComponentItemsContainer & IUiCfgContainerExpandable = {
  id: 'mock-component-items',
  kind: 'container',
  type: UICfgContainerType.ComponentItems,
  name: 'ComponentItems Container',
  enabled: true,
  title: 'Component Items Title (CnT)',
  description: 'Component items description - groups a component with its attributes (CnD)',
  expandable: true,
  defaultExpanded: true,
  showTitle: true,
  showDescription: true,
  combineIntoGrid: false,
  componentsSettings: UICfgComponentContainerElementOptionsDefault,
  attributesSettings: UICfgAttributesContainerElementOptionsDefault,
  children: [] as unknown as [IUICfgComponent, ...IUICfgAttributeText[]], // Will be populated with mock data
};

/**
 * Product merge container - displays products from multiple components
 * merged into a single view. Useful for showing product selection options.
 */
export const mockProductMergeContainer: IUICfgProductsMergeContainer = {
  id: 'mock-product-merge',
  kind: 'container',
  type: UICfgContainerType.ProductMerge,
  name: 'Products Container',
  enabled: true,
  title: 'Products Container Title (CnT)',
  description: 'Products container description - displays product selection cards (CnD)',
  expandable: true,
  defaultExpanded: true,
  showTitle: true,
  showDescription: true,
  combineIntoGrid: false,
  productsSettings: {
    ...UICfgProductsContainerElementOptionsDefault,
    layout: 'card',
    showImage: true,
    showTitle: true,
    showDescription: true,
  },
  children: [],
};

// ============================================================================
// Mock Component Data
// ============================================================================

/**
 * Sample component representing a configurable coffee machine.
 * Components define the main configurable entities in the product.
 */
export const mockComponent: IUICfgComponent = {
  id: 'mock-component-1',
  kind: 'component',
  type: 'default',
  name: 'Coffee Machine Model',
  enabled: true,
  source: {
    type: 'component',
    componentKey: 'coffee-machine',
  },
  uiControl: {
    showTitle: true,
    showDescription: true,
    showComponent: 'always',
    showSwitcher: false,
    products: {
      layout: 'card',
      showImage: true,
      showTitle: true,
      showDescription: true,
    },
  },
};

/**
 * Secondary component for demonstrating multiple components.
 */
export const mockComponentAccessory: IUICfgComponent = {
  id: 'mock-component-2',
  kind: 'component',
  type: 'default',
  name: 'Accessories',
  enabled: true,
  source: {
    type: 'component',
    componentKey: 'accessories',
  },
  uiControl: {
    showTitle: true,
    showDescription: false,
    showComponent: '2+',
    showSwitcher: true,
  },
};

// ============================================================================
// Mock Attribute Data
// ============================================================================

/**
 * Color attribute - demonstrates grid card type for visual selection.
 */
export const mockAttributeColor: IUICfgAttributeText = {
  id: 'mock-attr-color',
  kind: 'attribute',
  type: 'text',
  name: 'Color',
  enabled: true,
  source: {
    type: 'attribute',
    componentKey: 'coffee-machine',
    attributeKey: 'color',
  },
  uiControl: {
    showTitle: true,
    showDescription: true,
    showDetails: false,
    cardType: 'grid',
    layoutType: 'simple',
    itemOptions: {
      showTitle: true,
      showImage: true,
      showDescription: false,
    },
  },
};

/**
 * Size attribute - demonstrates list card type for detailed options.
 */
export const mockAttributeSize: IUICfgAttributeText = {
  id: 'mock-attr-size',
  kind: 'attribute',
  type: 'text',
  name: 'Size',
  enabled: true,
  source: {
    type: 'attribute',
    componentKey: 'coffee-machine',
    attributeKey: 'size',
  },
  uiControl: {
    showTitle: true,
    showDescription: true,
    showDetails: true,
    cardType: 'list',
    layoutType: 'detailed',
    itemOptions: {
      showTitle: true,
      showImage: false,
      showDescription: true,
    },
  },
};

/**
 * Material attribute - demonstrates grid layout with images.
 */
export const mockAttributeMaterial: IUICfgAttributeText = {
  id: 'mock-attr-material',
  kind: 'attribute',
  type: 'text',
  name: 'Material',
  enabled: true,
  source: {
    type: 'attribute',
    componentKey: 'coffee-machine',
    attributeKey: 'material',
  },
  uiControl: {
    showTitle: true,
    showDescription: false,
    showDetails: false,
    cardType: 'grid',
    layoutType: 'simple',
    itemOptions: {
      showTitle: true,
      showImage: true,
      showDescription: false,
    },
  },
};

/**
 * Combined array of all mock attributes for convenience.
 */
export const mockAttributes: IUICfgAttributeText[] = [mockAttributeColor, mockAttributeSize, mockAttributeMaterial];

// ============================================================================
// Mock Attribute Values for Display
// These represent the actual selectable values for attributes.
// ============================================================================

export interface MockAttributeValue {
  id: string;
  label: string;
  image?: string | null;
  description?: string;
}

/**
 * Attribute values organized by attribute key.
 * Used for rendering the actual selectable options in the UI.
 */
export const mockAttributeValues: Record<string, MockAttributeValue[]> = {
  color: [
    { id: 'red', label: 'Red (AV)', image: null },
    { id: 'blue', label: 'Blue (AV)', image: null },
    { id: 'black', label: 'Black (AV)', image: null },
    { id: 'white', label: 'White (AV)', image: null },
  ],
  size: [
    { id: 'small', label: 'Small (AV)', description: 'Compact size - perfect for small spaces (AVD)' },
    { id: 'medium', label: 'Medium (AV)', description: 'Standard size - ideal for most kitchens (AVD)' },
    { id: 'large', label: 'Large (AV)', description: 'Extra capacity - for heavy coffee drinkers (AVD)' },
  ],
  material: [
    { id: 'steel', label: 'Stainless Steel (AV)', description: 'Durable and modern (AVD)' },
    { id: 'plastic', label: 'Premium Plastic (AV)', description: 'Lightweight and affordable (AVD)' },
    { id: 'ceramic', label: 'Ceramic Finish (AV)', description: 'Elegant and easy to clean (AVD)' },
  ],
};

// ============================================================================
// Mock Product Display Data
// Simulates product data as it would appear in the configurator.
// ============================================================================

export interface MockProductDisplay {
  id: string;
  title: string;
  description: string;
  image: string | null;
  price?: string;
}

/**
 * Product display data for product merge container demos.
 */
export const mockProductDisplayData: MockProductDisplay[] = [
  {
    id: 'product-1',
    title: 'Basic Model (PT)',
    description: 'Entry-level coffee machine with essential features (PD)',
    image: null,
    price: '$199 (PP)',
  },
  {
    id: 'product-2',
    title: 'Pro Model (PT)',
    description: 'Professional grade coffee machine with advanced brewing options (PD)',
    image: null,
    price: '$399 (PP)',
  },
  {
    id: 'product-3',
    title: 'Premium Model (PT)',
    description: 'Top of the line coffee machine with all premium features (PD)',
    image: null,
    price: '$599 (PP)',
  },
];

// ============================================================================
// Mock Attribute Configs (for state-based rendering)
// These configs are used with MockAttributeState classes for interactive demos.
// ============================================================================

/**
 * Text attribute - Card layout (colors)
 * Demonstrates card-based visual selection for color options.
 */
export const mockColorAttributeConfig: MockTextAttributeConfig = {
  id: 'mock-color-attr',
  initialValue: 'value-1',
  availableValues: ['value-1', 'value-2', 'value-3', 'value-4'],
  viewElement: {
    id: 'color-attr',
    kind: 'attribute',
    type: 'text',
    name: 'Text Attribute (Card)',
    enabled: true,
    source: { type: 'attribute', componentKey: 'product', attributeKey: 'color' },
    uiControl: {
      layout: 'card',
      itemOptions: { showTitle: true, showImage: true, showDescription: false },
    },
  } as any,
  textData: {
    name: 'Attribute Title (AT)',
    description: 'Attribute description text (AD)',
    values: [
      { id: 1, value: 'value-1', label: 'Label 1 (AV)', description: 'tooltip 1 (AVD)', image: { url: '' } },
      { id: 2, value: 'value-2', label: 'Label 2 (AV)', description: 'tooltip 2 (AVD)', image: { url: '' } },
      { id: 3, value: 'value-3', label: 'Label 3 (AV)', description: 'tooltip 3 (AVD)', image: { url: '' } },
      { id: 4, value: 'value-4', label: 'Label 4 (AV)', description: 'tooltip 4 (AVD)', image: { url: '' } },
    ],
  },
};

/**
 * Text attribute - List layout (sizes)
 * Demonstrates list-based selection with descriptions.
 */
export const mockSizeAttributeConfig: MockTextAttributeConfig = {
  id: 'mock-size-attr',
  initialValue: 'value-2',
  availableValues: ['value-1', 'value-2', 'value-3', 'value-4'],
  viewElement: {
    id: 'size-attr',
    kind: 'attribute',
    type: 'text',
    name: 'Text Attribute (List)',
    enabled: true,
    source: { type: 'attribute', componentKey: 'product', attributeKey: 'size' },
    uiControl: {
      layout: 'list',
      itemOptions: { showTitle: true, showDescription: true },
    },
  } as any,
  textData: {
    name: 'Attribute Title (AT)',
    description: 'Attribute description text (AD)',
    values: [
      { id: 1, value: 'value-1', label: 'Small (AV)', description: 'description - small (AVD)', image: { url: '' } },
      { id: 2, value: 'value-2', label: 'Medium (AV)', description: 'description - medium (AVD)', image: { url: '' } },
      { id: 3, value: 'value-3', label: 'Large (AV)', description: 'description - large (AVD)', image: { url: '' } },
      { id: 4, value: 'value-4', label: 'XL (AV)', description: 'description - extra large (AVD)', image: { url: '' } },
    ],
  },
};

/**
 * Boolean attribute (toggle)
 * Demonstrates boolean toggle for warranty option.
 */
export const mockToggleAttributeConfig: MockBooleanAttributeConfig = {
  id: 'mock-toggle-attr',
  initialValue: true,
  availableValues: [true, false],
  viewElement: {
    id: 'warranty-attr',
    kind: 'attribute',
    type: 'boolean',
    name: 'Boolean Attribute',
    enabled: true,
    source: { type: 'attribute', componentKey: 'product', attributeKey: 'warranty' },
    uiControl: {},
  } as any,
  textData: {
    name: 'Attribute Title - Boolean (AT)',
    description: 'Attribute description text (AD)',
    values: [
      { id: 1, value: true, label: 'Yes (AV)', image: { url: '' } },
      { id: 2, value: false, label: 'No (AV)', image: { url: '' } },
    ],
  },
};

/**
 * Integer attribute (quantity)
 * Demonstrates numeric selection for quantity.
 */
export const mockQuantityAttributeConfig: MockIntegerAttributeConfig = {
  id: 'mock-quantity-attr',
  initialValue: 1,
  availableValues: [1, 2, 3, 4, 5],
  viewElement: {
    id: 'quantity-attr',
    kind: 'attribute',
    type: 'integer',
    name: 'Integer Attribute',
    enabled: true,
    source: { type: 'attribute', componentKey: 'product', attributeKey: 'quantity' },
    uiControl: {
      layout: 'list',
      itemOptions: { showTitle: true },
    },
  } as any,
  textData: {
    name: 'Attribute Title - Integer (AT)',
    description: 'Attribute description text (AD)',
    values: [
      { id: 1, value: 1, label: '1 (AV)', image: { url: '' } },
      { id: 2, value: 2, label: '2 (AV)', image: { url: '' } },
      { id: 3, value: 3, label: '3 (AV)', image: { url: '' } },
      { id: 4, value: 4, label: '4 (AV)', image: { url: '' } },
      { id: 5, value: 5, label: '5 (AV)', image: { url: '' } },
    ],
  },
};

// ============================================================================
// Mock Component Configs (for state-based rendering)
// These configs are used with MockComponentLookup for interactive component demos.
// ============================================================================

/**
 * Primary component configuration.
 * Demonstrates an optional component that is enabled by default.
 */
export const mockCoffeeMachineComponent: MockComponentConfig = {
  viewElement: {
    id: 'component-primary',
    kind: 'component',
    type: 'default',
    name: 'Component (Enabled)',
    enabled: true,
    source: { type: 'component', componentKey: 'component-1' },
    uiControl: { showTitle: true, showDescription: true },
  } as any,
  lookupConfig: {
    componentKey: 'component-1',
    isOptional: true,
    isEnabled: true,
    name: 'Component Title (CT)',
    description: 'Component description text (CD)',
  },
};

/**
 * Secondary component configuration.
 * Demonstrates an optional component that is disabled by default.
 */
export const mockAccessoryComponent: MockComponentConfig = {
  viewElement: {
    id: 'component-secondary',
    kind: 'component',
    type: 'default',
    name: 'Component (Disabled)',
    enabled: true,
    source: { type: 'component', componentKey: 'component-2' },
    uiControl: { showTitle: true, showDescription: true },
  } as any,
  lookupConfig: {
    componentKey: 'component-2',
    isOptional: true,
    isEnabled: false,
    name: 'Component Title (CT)',
    description: 'Component description text (CD)',
  },
};

// ============================================================================
// Spacing Tokens
// Design system spacing tokens organized by category and breakpoint.
// ============================================================================

export interface SpacingToken {
  name: string;
  description: string;
  values: {
    xs: string;
    sm: string;
    md: string;
    lg: string;
  };
}

export interface SpacingTokenCategory {
  category: string;
  description: string;
  tokens: SpacingToken[];
}

/**
 * Section-level spacing tokens - used for major layout divisions.
 */
export const sectionSpacingTokens: SpacingToken[] = [
  {
    name: '--gap-section',
    description: 'Gap between major sections',
    values: { xs: '40px', sm: '40px', md: '60px', lg: '80px' },
  },
  {
    name: '--gap-subsection',
    description: 'Gap between containers within a section',
    values: { xs: '24px', sm: '24px', md: '36px', lg: '48px' },
  },
];

/**
 * Item-level spacing tokens - used for elements within containers.
 */
export const itemSpacingTokens: SpacingToken[] = [
  {
    name: '--gap-section-subsection',
    description: 'Gap between section items',
    values: { xs: '8px', sm: '8px', md: '12px', lg: '16px' },
  },
  {
    name: '--margin-list',
    description: 'Margin between list items',
    values: { xs: '4px', sm: '4px', md: '8px', lg: '12px' },
  },
];

/**
 * Padding tokens - internal spacing within containers and elements.
 */
export const paddingTokens: SpacingToken[] = [
  {
    name: '--padding-option',
    description: 'Option card top padding',
    values: { xs: '16px', sm: '16px', md: '20px', lg: '24px' },
  },
  {
    name: '--padding-option-bottom',
    description: 'Option card bottom padding',
    values: { xs: '8px', sm: '8px', md: '12px', lg: '16px' },
  },
  {
    name: '--padding-screen',
    description: 'Screen edge padding',
    values: { xs: '24px', sm: '24px', md: '40px', lg: '80px' },
  },
  {
    name: '--padding-grid',
    description: 'Grid internal padding',
    values: { xs: '8px', sm: '8px', md: '8px', lg: '8px' },
  },
  {
    name: '--padding-grid-side',
    description: 'Grid side padding',
    values: { xs: '16px', sm: '16px', md: '32px', lg: '72px' },
  },
];

/**
 * Organized spacing tokens by category for the spacing visualization section.
 */
export const spacingTokens: SpacingTokenCategory[] = [
  {
    category: 'Section Spacing',
    description: 'Spacing between major layout sections',
    tokens: sectionSpacingTokens,
  },
  {
    category: 'Item Spacing',
    description: 'Spacing between items within containers',
    tokens: itemSpacingTokens,
  },
  {
    category: 'Padding',
    description: 'Internal padding for containers and elements',
    tokens: paddingTokens,
  },
];

/**
 * Legacy format for backward compatibility with simpler token lookup.
 */
export const spacingTokensSimple = {
  section: sectionSpacingTokens,
  item: itemSpacingTokens,
  padding: paddingTokens,
};

// ============================================================================
// Complete Mock UI Config
// A fully populated root container demonstrating the full hierarchy.
// ============================================================================

/**
 * Creates a complete mock UI configuration with nested containers,
 * components, and attributes. Useful for full-page demos.
 */
export const createCompleteMockUIConfig = (): IUICfgRootContainer => {
  const colorAttr = { ...mockAttributeColor };
  const sizeAttr = { ...mockAttributeSize };
  const materialAttr = { ...mockAttributeMaterial };
  const component = { ...mockComponent };

  const componentItemsContainer: IUICfgComponentItemsContainer = {
    ...mockComponentItemsContainer,
    children: [component, colorAttr, sizeAttr] as [IUICfgComponent, ...IUICfgAttributeText[]],
  };

  const mixedContainer: IUICfgMixedContainer = {
    ...mockMixedContainer,
    id: 'complete-mixed-1',
    children: [materialAttr],
  };

  return {
    ...mockRootContainer,
    id: 'complete-root',
    children: [componentItemsContainer, mixedContainer],
  };
};

// ============================================================================
// Card Variation Configs - Grid Layout
// These demonstrate different combinations of showTitle, showImage, showDescription
// ============================================================================

/**
 * Grid layout - Full (Image + Title + Description)
 */
export const mockCardGridFull: MockTextAttributeConfig = {
  id: 'card-grid-full',
  initialValue: 'value-2',
  availableValues: ['value-1', 'value-2', 'value-3', 'value-4'],
  viewElement: {
    id: 'grid-full-attr',
    kind: 'attribute',
    type: 'text',
    name: 'Grid: Full',
    enabled: true,
    source: { type: 'attribute', componentKey: 'product', attributeKey: 'color' },
    uiControl: {
      layout: 'card',
      itemOptions: { showTitle: true, showImage: true, showDescription: true },
    },
  } as any,
  textData: {
    name: 'Grid: Image + Title + Description (AT)',
    description: 'All card elements visible (AD)',
    values: [
      {
        id: 1,
        value: 'value-1',
        label: 'Red (AV)',
        description: 'Vibrant red color (AVD)',
        image: { url: PLACEHOLDER_IMAGES.red },
      },
      {
        id: 2,
        value: 'value-2',
        label: 'Blue (AV)',
        description: 'Ocean blue shade (AVD)',
        image: { url: PLACEHOLDER_IMAGES.blue },
      },
      {
        id: 3,
        value: 'value-3',
        label: 'Green (AV)',
        description: 'Forest green tone (AVD)',
        image: { url: PLACEHOLDER_IMAGES.green },
      },
      {
        id: 4,
        value: 'value-4',
        label: 'Yellow (AV)',
        description: 'Sunny yellow hue (AVD)',
        image: { url: PLACEHOLDER_IMAGES.yellow },
      },
    ],
  },
};

/**
 * Grid layout - Image + Title (no description)
 */
export const mockCardGridImageTitle: MockTextAttributeConfig = {
  id: 'card-grid-image-title',
  initialValue: 'value-3',
  availableValues: ['value-1', 'value-2', 'value-3', 'value-4'],
  viewElement: {
    id: 'grid-image-title-attr',
    kind: 'attribute',
    type: 'text',
    name: 'Grid: Image + Title',
    enabled: true,
    source: { type: 'attribute', componentKey: 'product', attributeKey: 'color' },
    uiControl: {
      layout: 'card',
      itemOptions: { showTitle: true, showImage: true, showDescription: false },
    },
  } as any,
  textData: {
    name: 'Grid: Image + Title (AT)',
    description: 'No description shown (AD)',
    values: [
      {
        id: 1,
        value: 'value-1',
        label: 'Red (AV)',
        description: 'Hidden description (AVD)',
        image: { url: PLACEHOLDER_IMAGES.red },
      },
      {
        id: 2,
        value: 'value-2',
        label: 'Blue (AV)',
        description: 'Hidden description (AVD)',
        image: { url: PLACEHOLDER_IMAGES.blue },
      },
      {
        id: 3,
        value: 'value-3',
        label: 'Green (AV)',
        description: 'Hidden description (AVD)',
        image: { url: PLACEHOLDER_IMAGES.green },
      },
      {
        id: 4,
        value: 'value-4',
        label: 'Yellow (AV)',
        description: 'Hidden description (AVD)',
        image: { url: PLACEHOLDER_IMAGES.yellow },
      },
    ],
  },
};

/**
 * Grid layout - Title only (no image)
 */
export const mockCardGridTitleOnly: MockTextAttributeConfig = {
  id: 'card-grid-title-only',
  initialValue: 'value-2',
  availableValues: ['value-1', 'value-2', 'value-3', 'value-4'],
  viewElement: {
    id: 'grid-title-only-attr',
    kind: 'attribute',
    type: 'text',
    name: 'Grid: Title Only',
    enabled: true,
    source: { type: 'attribute', componentKey: 'product', attributeKey: 'size' },
    uiControl: {
      layout: 'card',
      itemOptions: { showTitle: true, showImage: false, showDescription: false },
    },
  } as any,
  textData: {
    name: 'Grid: Title Only (AT)',
    description: 'No image or description (AD)',
    values: [
      { id: 1, value: 'value-1', label: 'Small (AV)', image: { url: '' } },
      { id: 2, value: 'value-2', label: 'Medium (AV)', image: { url: '' } },
      { id: 3, value: 'value-3', label: 'Large (AV)', image: { url: '' } },
      { id: 4, value: 'value-4', label: 'X-Large (AV)', image: { url: '' } },
    ],
  },
};

/**
 * Grid layout - Image only (no title)
 */
export const mockCardGridImageOnly: MockTextAttributeConfig = {
  id: 'card-grid-image-only',
  initialValue: 'value-3',
  availableValues: ['value-1', 'value-2', 'value-3', 'value-4'],
  viewElement: {
    id: 'grid-image-only-attr',
    kind: 'attribute',
    type: 'text',
    name: 'Grid: Image Only',
    enabled: true,
    source: { type: 'attribute', componentKey: 'product', attributeKey: 'color' },
    uiControl: {
      layout: 'card',
      itemOptions: { showTitle: false, showImage: true, showDescription: false },
    },
  } as any,
  textData: {
    name: 'Grid: Image Only (AT)',
    description: 'Only image visible (color swatches) (AD)',
    values: [
      { id: 1, value: 'value-1', label: 'Red (AV)', image: { url: PLACEHOLDER_IMAGES.red } },
      { id: 2, value: 'value-2', label: 'Blue (AV)', image: { url: PLACEHOLDER_IMAGES.blue } },
      { id: 3, value: 'value-3', label: 'Green (AV)', image: { url: PLACEHOLDER_IMAGES.green } },
      { id: 4, value: 'value-4', label: 'Purple (AV)', image: { url: PLACEHOLDER_IMAGES.purple } },
    ],
  },
};

// ============================================================================
// Card Variation Configs - List Layout
// ============================================================================

/**
 * List layout - Full (Image + Title + Description)
 */
export const mockCardListFull: MockTextAttributeConfig = {
  id: 'card-list-full',
  initialValue: 'value-2',
  availableValues: ['value-1', 'value-2', 'value-3', 'value-4'],
  viewElement: {
    id: 'list-full-attr',
    kind: 'attribute',
    type: 'text',
    name: 'List: Full',
    enabled: true,
    source: { type: 'attribute', componentKey: 'product', attributeKey: 'material' },
    uiControl: {
      layout: 'list',
      itemOptions: { showTitle: true, showImage: true, showDescription: true },
    },
  } as any,
  textData: {
    name: 'List: Image + Title + Description (AT)',
    description: 'All card elements visible in horizontal layout (AD)',
    values: [
      {
        id: 1,
        value: 'value-1',
        label: 'Steel (AV)',
        description: 'Durable stainless steel finish (AVD)',
        image: { url: PLACEHOLDER_IMAGES.gray },
      },
      {
        id: 2,
        value: 'value-2',
        label: 'Wood (AV)',
        description: 'Natural oak wood grain (AVD)',
        image: { url: PLACEHOLDER_IMAGES.orange },
      },
      {
        id: 3,
        value: 'value-3',
        label: 'Fabric (AV)',
        description: 'Soft premium fabric cover (AVD)',
        image: { url: PLACEHOLDER_IMAGES.blue },
      },
      {
        id: 4,
        value: 'value-4',
        label: 'Leather (AV)',
        description: 'Genuine Italian leather (AVD)',
        image: { url: PLACEHOLDER_IMAGES.purple },
      },
    ],
  },
};

/**
 * List layout - Title + Description (no image)
 */
export const mockCardListTitleDesc: MockTextAttributeConfig = {
  id: 'card-list-title-desc',
  initialValue: 'value-3',
  availableValues: ['value-1', 'value-2', 'value-3', 'value-4'],
  viewElement: {
    id: 'list-title-desc-attr',
    kind: 'attribute',
    type: 'text',
    name: 'List: Title + Description',
    enabled: true,
    source: { type: 'attribute', componentKey: 'product', attributeKey: 'size' },
    uiControl: {
      layout: 'list',
      itemOptions: { showTitle: true, showImage: false, showDescription: true },
    },
  } as any,
  textData: {
    name: 'List: Title + Description (AT)',
    description: 'No image shown (AD)',
    values: [
      {
        id: 1,
        value: 'value-1',
        label: 'Compact (AV)',
        description: 'Perfect for small spaces - 60cm width (AVD)',
        image: { url: '' },
      },
      {
        id: 2,
        value: 'value-2',
        label: 'Standard (AV)',
        description: 'Most popular size - 90cm width (AVD)',
        image: { url: '' },
      },
      {
        id: 3,
        value: 'value-3',
        label: 'Large (AV)',
        description: 'Extra capacity - 120cm width (AVD)',
        image: { url: '' },
      },
      {
        id: 4,
        value: 'value-4',
        label: 'Extra Large (AV)',
        description: 'Maximum storage - 150cm width (AVD)',
        image: { url: '' },
      },
    ],
  },
};

/**
 * List layout - Title only
 */
export const mockCardListTitleOnly: MockTextAttributeConfig = {
  id: 'card-list-title-only',
  initialValue: 'value-2',
  availableValues: ['value-1', 'value-2', 'value-3', 'value-4'],
  viewElement: {
    id: 'list-title-only-attr',
    kind: 'attribute',
    type: 'text',
    name: 'List: Title Only',
    enabled: true,
    source: { type: 'attribute', componentKey: 'product', attributeKey: 'option' },
    uiControl: {
      layout: 'list',
      itemOptions: { showTitle: true, showImage: false, showDescription: false },
    },
  } as any,
  textData: {
    name: 'List: Title Only (AT)',
    description: 'Minimal list layout (AD)',
    values: [
      { id: 1, value: 'value-1', label: 'Option A (AV)', image: { url: '' } },
      { id: 2, value: 'value-2', label: 'Option B (AV)', image: { url: '' } },
      { id: 3, value: 'value-3', label: 'Option C (AV)', image: { url: '' } },
      { id: 4, value: 'value-4', label: 'Option D (AV)', image: { url: '' } },
    ],
  },
};

/**
 * List layout - Image + Title (no description)
 */
export const mockCardListImageTitle: MockTextAttributeConfig = {
  id: 'card-list-image-title',
  initialValue: 'value-4',
  availableValues: ['value-1', 'value-2', 'value-3', 'value-4'],
  viewElement: {
    id: 'list-image-title-attr',
    kind: 'attribute',
    type: 'text',
    name: 'List: Image + Title',
    enabled: true,
    source: { type: 'attribute', componentKey: 'product', attributeKey: 'color' },
    uiControl: {
      layout: 'list',
      itemOptions: { showTitle: true, showImage: true, showDescription: false },
    },
  } as any,
  textData: {
    name: 'List: Image + Title (AT)',
    description: 'No description shown (AD)',
    values: [
      { id: 1, value: 'value-1', label: 'Crimson Red (AV)', image: { url: PLACEHOLDER_IMAGES.red } },
      { id: 2, value: 'value-2', label: 'Ocean Blue (AV)', image: { url: PLACEHOLDER_IMAGES.blue } },
      { id: 3, value: 'value-3', label: 'Forest Green (AV)', image: { url: PLACEHOLDER_IMAGES.green } },
      { id: 4, value: 'value-4', label: 'Royal Purple (AV)', image: { url: PLACEHOLDER_IMAGES.purple } },
    ],
  },
};

// ============================================================================
// Attribute with Details Panel Configs
// Demonstrates showDetails, showDetailsImage, showDetailsTitle, showDetailsDescription
// ============================================================================

/**
 * Attribute with details - Full (Image + Title + Description)
 */
export const mockAttributeDetailsFull: MockTextAttributeConfig = {
  id: 'attr-details-full',
  initialValue: 'value-2',
  availableValues: ['value-1', 'value-2', 'value-3'],
  viewElement: {
    id: 'details-full-attr',
    kind: 'attribute',
    type: 'text',
    name: 'Details: Full',
    enabled: true,
    source: { type: 'attribute', componentKey: 'product', attributeKey: 'finish' },
    uiControl: {
      layout: 'card',
      showDetails: true,
      showDetailsImage: true,
      showDetailsTitle: true,
      showDetailsDescription: true,
      itemOptions: { showTitle: true, showImage: true, showDescription: false },
    },
  } as any,
  textData: {
    name: 'With Details: Image + Title + Description (AT)',
    description: 'Select to see full details below (AD)',
    values: [
      {
        id: 1,
        value: 'value-1',
        label: 'Matte (AV)',
        description:
          'Smooth matte finish with low reflectivity. Perfect for modern interiors with minimal glare. (AVD)',
        image: { url: PLACEHOLDER_IMAGES.gray },
      },
      {
        id: 2,
        value: 'value-2',
        label: 'Glossy (AV)',
        description:
          'High-shine glossy finish that reflects light beautifully. Creates a sleek, contemporary look. (AVD)',
        image: { url: PLACEHOLDER_IMAGES.blue },
      },
      {
        id: 3,
        value: 'value-3',
        label: 'Satin (AV)',
        description: 'Subtle satin sheen between matte and glossy. Elegant choice for premium applications. (AVD)',
        image: { url: PLACEHOLDER_IMAGES.purple },
      },
    ],
  },
};

/**
 * Attribute with details - Title + Description only
 */
export const mockAttributeDetailsTitleDesc: MockTextAttributeConfig = {
  id: 'attr-details-title-desc',
  initialValue: 'value-2',
  availableValues: ['value-1', 'value-2', 'value-3'],
  viewElement: {
    id: 'details-title-desc-attr',
    kind: 'attribute',
    type: 'text',
    name: 'Details: Title + Desc',
    enabled: true,
    source: { type: 'attribute', componentKey: 'product', attributeKey: 'warranty' },
    uiControl: {
      layout: 'card',
      showDetails: true,
      showDetailsImage: false,
      showDetailsTitle: true,
      showDetailsDescription: true,
      itemOptions: { showTitle: true, showImage: false, showDescription: false },
    },
  } as any,
  textData: {
    name: 'With Details: Title + Description (AT)',
    description: 'Details panel without image (AD)',
    values: [
      {
        id: 1,
        value: 'value-1',
        label: '1 Year (AV)',
        description: 'Standard warranty covering manufacturing defects for 12 months from purchase date. (AVD)',
        image: { url: '' },
      },
      {
        id: 2,
        value: 'value-2',
        label: '3 Years (AV)',
        description: 'Extended warranty with full coverage including parts and labor for 36 months. (AVD)',
        image: { url: '' },
      },
      {
        id: 3,
        value: 'value-3',
        label: '5 Years (AV)',
        description: 'Premium warranty with comprehensive protection, on-site service, and priority support. (AVD)',
        image: { url: '' },
      },
    ],
  },
};

/**
 * Attribute with details - Title only
 */
export const mockAttributeDetailsTitleOnly: MockTextAttributeConfig = {
  id: 'attr-details-title-only',
  initialValue: 'value-2',
  availableValues: ['value-1', 'value-2', 'value-3'],
  viewElement: {
    id: 'details-title-only-attr',
    kind: 'attribute',
    type: 'text',
    name: 'Details: Title Only',
    enabled: true,
    source: { type: 'attribute', componentKey: 'product', attributeKey: 'tier' },
    uiControl: {
      layout: 'card',
      showDetails: true,
      showDetailsImage: false,
      showDetailsTitle: true,
      showDetailsDescription: false,
      itemOptions: { showTitle: true, showImage: false, showDescription: false },
    },
  } as any,
  textData: {
    name: 'With Details: Title Only (AT)',
    description: 'Minimal details panel (AD)',
    values: [
      { id: 1, value: 'value-1', label: 'Basic (AV)', image: { url: '' } },
      { id: 2, value: 'value-2', label: 'Professional (AV)', image: { url: '' } },
      { id: 3, value: 'value-3', label: 'Enterprise (AV)', image: { url: '' } },
    ],
  },
};

// ============================================================================
// Container Title Variations
// Demonstrates showTitle and showDescription settings on containers
// ============================================================================

/**
 * Container with title and description visible
 */
export const mockContainerTitleDesc: IUICfgMixedContainer = {
  id: 'container-title-desc',
  kind: 'container',
  type: UICfgContainerType.Mixed,
  name: 'Container with Title + Description',
  enabled: true,
  title: 'Container Title (CnT)',
  description: 'Container description text - explains the purpose of this section (CnD)',
  expandable: true,
  defaultExpanded: true,
  showTitle: true,
  showDescription: true,
  combineIntoGrid: false,
  componentsSettings: UICfgComponentContainerElementOptionsDefault,
  attributesSettings: UICfgAttributesContainerElementOptionsDefault,
  children: [],
};

/**
 * Container with title only (no description)
 */
export const mockContainerTitleOnly: IUICfgMixedContainer = {
  id: 'container-title-only',
  kind: 'container',
  type: UICfgContainerType.Mixed,
  name: 'Container with Title Only',
  enabled: true,
  title: 'Container Title Only (CnT)',
  description: 'This description should be hidden (CnD)',
  expandable: true,
  defaultExpanded: true,
  showTitle: true,
  showDescription: false,
  combineIntoGrid: false,
  componentsSettings: UICfgComponentContainerElementOptionsDefault,
  attributesSettings: UICfgAttributesContainerElementOptionsDefault,
  children: [],
};

/**
 * Container with no title (content only)
 */
export const mockContainerNoTitle: IUICfgMixedContainer = {
  id: 'container-no-title',
  kind: 'container',
  type: UICfgContainerType.Mixed,
  name: 'Container with No Title',
  enabled: true,
  title: 'Hidden Title (CnT)',
  description: 'Hidden Description (CnD)',
  expandable: true,
  defaultExpanded: true,
  showTitle: false,
  showDescription: false,
  combineIntoGrid: false,
  componentsSettings: UICfgComponentContainerElementOptionsDefault,
  attributesSettings: UICfgAttributesContainerElementOptionsDefault,
  children: [],
};

// ============================================================================
// Container State Variations
// Demonstrates expandable, defaultExpanded settings on containers
// ============================================================================

/**
 * Container that is expanded by default
 */
export const mockContainerExpandedByDefault: IUICfgMixedContainer = {
  id: 'container-expanded-default',
  kind: 'container',
  type: UICfgContainerType.Mixed,
  name: 'Container Expanded by Default',
  enabled: true,
  title: 'Expanded by Default (CnT)',
  description: 'This container starts in expanded state (CnD)',
  expandable: true,
  defaultExpanded: true,
  showTitle: true,
  showDescription: true,
  combineIntoGrid: false,
  componentsSettings: UICfgComponentContainerElementOptionsDefault,
  attributesSettings: UICfgAttributesContainerElementOptionsDefault,
  children: [],
};

/**
 * Container that is collapsed by default
 */
export const mockContainerCollapsedByDefault: IUICfgMixedContainer = {
  id: 'container-collapsed-default',
  kind: 'container',
  type: UICfgContainerType.Mixed,
  name: 'Container Collapsed by Default',
  enabled: true,
  title: 'Collapsed by Default (CnT)',
  description: 'This container starts in collapsed state (CnD)',
  expandable: true,
  defaultExpanded: false,
  showTitle: true,
  showDescription: true,
  combineIntoGrid: false,
  componentsSettings: UICfgComponentContainerElementOptionsDefault,
  attributesSettings: UICfgAttributesContainerElementOptionsDefault,
  children: [],
};

/**
 * Container that is non-expandable (always visible)
 */
export const mockContainerNonExpandable: IUICfgMixedContainer = {
  id: 'container-non-expandable',
  kind: 'container',
  type: UICfgContainerType.Mixed,
  name: 'Non-Expandable Container',
  enabled: true,
  title: 'Non-Expandable Container (CnT)',
  description: 'This container cannot be collapsed - content is always visible (CnD)',
  expandable: false,
  defaultExpanded: true,
  showTitle: true,
  showDescription: true,
  combineIntoGrid: false,
  componentsSettings: UICfgComponentContainerElementOptionsDefault,
  attributesSettings: UICfgAttributesContainerElementOptionsDefault,
  children: [],
};

// ============================================================================
// Nested Container Variations
// Demonstrates 3-level nesting: Root -> Mixed -> Mixed -> ComponentItems
// ============================================================================

/**
 * Level 2 Mixed Container (expanded) for nested demo
 */
export const mockNestedMixedLevel2: IUICfgMixedContainer = {
  id: 'nested-mixed-level-2',
  kind: 'container',
  type: UICfgContainerType.Mixed,
  name: 'Nested Level 2 (Expanded)',
  enabled: true,
  title: 'Level 2: Mixed Container (CnT)',
  description: 'Second level container - expanded by default (CnD)',
  expandable: true,
  defaultExpanded: true,
  showTitle: true,
  showDescription: true,
  combineIntoGrid: false,
  componentsSettings: UICfgComponentContainerElementOptionsDefault,
  attributesSettings: UICfgAttributesContainerElementOptionsDefault,
  children: [],
};

/**
 * Level 3 Mixed Container (collapsed) for nested demo
 */
export const mockNestedMixedLevel3: IUICfgMixedContainer = {
  id: 'nested-mixed-level-3',
  kind: 'container',
  type: UICfgContainerType.Mixed,
  name: 'Nested Level 3 (Collapsed)',
  enabled: true,
  title: 'Level 3: Mixed Container (CnT)',
  description: 'Third level container - collapsed by default (CnD)',
  expandable: true,
  defaultExpanded: false,
  showTitle: true,
  showDescription: true,
  combineIntoGrid: false,
  componentsSettings: UICfgComponentContainerElementOptionsDefault,
  attributesSettings: UICfgAttributesContainerElementOptionsDefault,
  children: [],
};

/**
 * Level 4 Component Items Container for nested demo
 */
// The Component Items container type does not mix in `IUiCfgContainerExpandable`, but this
// fixture supplies those fields on purpose so the style guide exercises title/expand rendering.
export const mockNestedComponentItems: IUICfgComponentItemsContainer & IUiCfgContainerExpandable = {
  id: 'nested-component-items',
  kind: 'container',
  type: UICfgContainerType.ComponentItems,
  name: 'Nested ComponentItems',
  enabled: true,
  title: 'Level 4: Component Items (CnT)',
  description: 'Deepest level - component items container (CnD)',
  expandable: true,
  defaultExpanded: true,
  showTitle: true,
  showDescription: true,
  combineIntoGrid: false,
  componentsSettings: UICfgComponentContainerElementOptionsDefault,
  attributesSettings: UICfgAttributesContainerElementOptionsDefault,
  children: [] as unknown as [IUICfgComponent, ...IUICfgAttributeText[]],
};

// ============================================================================
// Rich Container Children Configs
// These configs have showTitle and showDescription enabled for use as
// comprehensive content inside container demos.
// ============================================================================

/**
 * Rich text attribute with visible title and description (Grid layout with images)
 * Used inside container demos to show how attributes render with full metadata.
 */
export const mockRichColorAttribute: MockTextAttributeConfig = {
  id: 'rich-color-attr',
  initialValue: 'value-1',
  availableValues: ['value-1', 'value-2', 'value-3'],
  viewElement: {
    id: 'rich-color-attr',
    kind: 'attribute',
    type: 'text',
    name: 'Color Selection',
    enabled: true,
    source: { type: 'attribute', componentKey: 'product', attributeKey: 'color' },
    uiControl: {
      showTitle: true,
      showDescription: true,
      layout: 'card',
      itemOptions: { showTitle: true, showImage: true, showDescription: false },
    },
  } as any,
  textData: {
    name: 'Color (AT)',
    description: 'Select your preferred color from available options (AD)',
    values: [
      {
        id: 1,
        value: 'value-1',
        label: 'Midnight Blue (AV)',
        description: 'Deep ocean blue (AVD)',
        image: { url: PLACEHOLDER_IMAGES.blue },
      },
      {
        id: 2,
        value: 'value-2',
        label: 'Forest Green (AV)',
        description: 'Natural green tone (AVD)',
        image: { url: PLACEHOLDER_IMAGES.green },
      },
      {
        id: 3,
        value: 'value-3',
        label: 'Sunset Red (AV)',
        description: 'Warm red shade (AVD)',
        image: { url: PLACEHOLDER_IMAGES.red },
      },
    ],
  },
};

/**
 * Rich text attribute with visible title and description (List layout)
 * Shows detailed options with descriptions in list format.
 */
export const mockRichSizeAttribute: MockTextAttributeConfig = {
  id: 'rich-size-attr',
  initialValue: 'value-2',
  availableValues: ['value-1', 'value-2', 'value-3'],
  viewElement: {
    id: 'rich-size-attr',
    kind: 'attribute',
    type: 'text',
    name: 'Size Selection',
    enabled: true,
    source: { type: 'attribute', componentKey: 'product', attributeKey: 'size' },
    uiControl: {
      showTitle: true,
      showDescription: true,
      layout: 'list',
      itemOptions: { showTitle: true, showDescription: true },
    },
  } as any,
  textData: {
    name: 'Size (AT)',
    description: 'Choose the size that best fits your space (AD)',
    values: [
      {
        id: 1,
        value: 'value-1',
        label: 'Compact (AV)',
        description: 'Perfect for small spaces - 60cm width (AVD)',
        image: { url: '' },
      },
      {
        id: 2,
        value: 'value-2',
        label: 'Standard (AV)',
        description: 'Most popular choice - 90cm width (AVD)',
        image: { url: '' },
      },
      {
        id: 3,
        value: 'value-3',
        label: 'Large (AV)',
        description: 'Maximum capacity - 120cm width (AVD)',
        image: { url: '' },
      },
    ],
  },
};

/**
 * Rich boolean attribute with visible title and description
 * Shows toggle with full metadata.
 */
export const mockRichBooleanAttribute: MockBooleanAttributeConfig = {
  id: 'rich-boolean-attr',
  initialValue: true,
  availableValues: [true, false],
  viewElement: {
    id: 'rich-warranty-attr',
    kind: 'attribute',
    type: 'boolean',
    name: 'Extended Warranty',
    enabled: true,
    source: { type: 'attribute', componentKey: 'product', attributeKey: 'warranty' },
    uiControl: {
      showTitle: true,
      showDescription: true,
    },
  } as any,
  textData: {
    name: 'Extended Warranty (AT)',
    description: 'Add 2 years of extended coverage for peace of mind (AD)',
    values: [
      { id: 1, value: true, label: 'Yes, add warranty (+$49) (AV)', image: { url: '' } },
      { id: 2, value: false, label: 'No thanks (AV)', image: { url: '' } },
    ],
  },
};

/**
 * Rich integer attribute with visible title and description
 * Shows quantity selector with full metadata.
 */
export const mockRichIntegerAttribute: MockIntegerAttributeConfig = {
  id: 'rich-integer-attr',
  initialValue: 2,
  availableValues: [1, 2, 3, 4, 5],
  viewElement: {
    id: 'rich-quantity-attr',
    kind: 'attribute',
    type: 'integer',
    name: 'Quantity',
    enabled: true,
    source: { type: 'attribute', componentKey: 'product', attributeKey: 'quantity' },
    uiControl: {
      showTitle: true,
      showDescription: true,
      layout: 'list',
      itemOptions: { showTitle: true },
    },
  } as any,
  textData: {
    name: 'Quantity (AT)',
    description: 'Select how many units you need (AD)',
    values: [
      { id: 1, value: 1, label: '1 unit (AV)', image: { url: '' } },
      { id: 2, value: 2, label: '2 units (AV)', image: { url: '' } },
      { id: 3, value: 3, label: '3 units (AV)', image: { url: '' } },
      { id: 4, value: 4, label: '4 units (AV)', image: { url: '' } },
      { id: 5, value: 5, label: '5 units (AV)', image: { url: '' } },
    ],
  },
};

/**
 * Rich component configuration with visible title and description
 * Used for demonstrating component toggles inside containers.
 */
export const mockRichComponent: MockComponentConfig = {
  viewElement: {
    id: 'rich-component',
    kind: 'component',
    type: 'default',
    name: 'Coffee Machine',
    enabled: true,
    source: { type: 'component', componentKey: 'rich-component' },
    uiControl: { showTitle: true, showDescription: true, showSwitcher: true },
  } as any,
  lookupConfig: {
    componentKey: 'rich-component',
    isOptional: true,
    isEnabled: true,
    name: 'Premium Coffee Machine (CT)',
    description: 'High-end espresso machine with automatic milk frothing (CD)',
  },
};

/**
 * Rich material attribute for nested container demos
 * Grid layout with images for visual selection.
 */
export const mockRichMaterialAttribute: MockTextAttributeConfig = {
  id: 'rich-material-attr',
  initialValue: 'value-1',
  availableValues: ['value-1', 'value-2', 'value-3'],
  viewElement: {
    id: 'rich-material-attr',
    kind: 'attribute',
    type: 'text',
    name: 'Material',
    enabled: true,
    source: { type: 'attribute', componentKey: 'product', attributeKey: 'material' },
    uiControl: {
      showTitle: true,
      showDescription: true,
      layout: 'card',
      itemOptions: { showTitle: true, showImage: true, showDescription: false },
    },
  } as any,
  textData: {
    name: 'Material (AT)',
    description: 'Choose the finish material for your product (AD)',
    values: [
      {
        id: 1,
        value: 'value-1',
        label: 'Brushed Steel (AV)',
        description: 'Durable metal finish (AVD)',
        image: { url: PLACEHOLDER_IMAGES.gray },
      },
      {
        id: 2,
        value: 'value-2',
        label: 'Natural Oak (AV)',
        description: 'Warm wood grain (AVD)',
        image: { url: PLACEHOLDER_IMAGES.orange },
      },
      {
        id: 3,
        value: 'value-3',
        label: 'Matte Black (AV)',
        description: 'Modern dark finish (AVD)',
        image: { url: PLACEHOLDER_IMAGES.purple },
      },
    ],
  },
};

/**
 * Rich finish attribute for nested container demos
 * List layout with detailed descriptions.
 */
export const mockRichFinishAttribute: MockTextAttributeConfig = {
  id: 'rich-finish-attr',
  initialValue: 'value-2',
  availableValues: ['value-1', 'value-2', 'value-3'],
  viewElement: {
    id: 'rich-finish-attr',
    kind: 'attribute',
    type: 'text',
    name: 'Surface Finish',
    enabled: true,
    source: { type: 'attribute', componentKey: 'product', attributeKey: 'finish' },
    uiControl: {
      showTitle: true,
      showDescription: true,
      layout: 'list',
      itemOptions: { showTitle: true, showDescription: true },
    },
  } as any,
  textData: {
    name: 'Surface Finish (AT)',
    description: 'Select the surface treatment for your product (AD)',
    values: [
      {
        id: 1,
        value: 'value-1',
        label: 'Matte (AV)',
        description: 'Low reflectivity, hides fingerprints (AVD)',
        image: { url: '' },
      },
      {
        id: 2,
        value: 'value-2',
        label: 'Satin (AV)',
        description: 'Subtle sheen, easy to clean (AVD)',
        image: { url: '' },
      },
      {
        id: 3,
        value: 'value-3',
        label: 'Glossy (AV)',
        description: 'High shine, premium look (AVD)',
        image: { url: '' },
      },
    ],
  },
};

// ============================================================================
// Composite Product Info Mock (for Full Example)
// ============================================================================

/**
 * Mock configuration for CompositeProductInfoUIElement.
 * Displays the main product information header with title and description.
 */
export const mockCompositeProductInfo: MockCompositeProductInfoConfig = {
  viewElement: {
    id: 'composite-product-info-1',
    kind: 'ui-element',
    type: 'composite-product-info',
    name: 'Product Information',
    enabled: true,
    showTitle: true,
    showDescription: true,
    showShortDescription: true,
    showImage: true,
  } as IUICfgUICompositeProductInfoElementUI,
  productData: {
    productKey: 'coffee-machine-pro',
    texts: {
      name: 'Composite Product Name (CPT)',
      description:
        'Professional-grade espresso machine with automatic milk frothing, temperature control, and 15-bar pressure system. Perfect for coffee enthusiasts who want barista-quality drinks at home. (CPD)',
      shortDescription: 'Professional espresso machine with advanced features (CPSD)',
    },
  },
};

// ============================================================================
// Component For Merge Configs (for ProductMergeContainer in Full Example)
// These use real ComponentElementState with mock API for instanceof checks.
// ============================================================================

/**
 * First component for ProductMergeContainer - Coffee Machines.
 * Products from this component will be merged with the second component.
 */
export const mockMergeComponent1Config: MockComponentForMergeConfig = {
  componentKey: 'coffee-machines',
  viewElement: {
    id: 'merge-component-1',
    kind: 'component',
    type: 'default',
    name: 'Coffee Machine',
    enabled: true,
    source: { type: 'component', componentKey: 'coffee-machines' },
    uiControl: { showTitle: true, showDescription: true, showForTwoOrMore: false },
  } as IUICfgComponent,
  products: [
    {
      productId: 1001,
      productKey: 'cm-espresso',
      texts: {
        name: 'Espresso Pro (PT)',
        description: 'Professional espresso machine with 15-bar pressure (PD)',
        shortDescription: '15-bar pressure system (PSD)',
      },
      images: [{ url: PLACEHOLDER_IMAGES.purple }],
    },
    {
      productId: 1002,
      productKey: 'cm-drip',
      texts: {
        name: 'Drip Master (PT)',
        description: 'Classic drip coffee maker with programmable timer (PD)',
        shortDescription: 'Programmable timer (PSD)',
      },
      images: [{ url: PLACEHOLDER_IMAGES.blue }],
    },
  ],
  textData: {
    name: 'Coffee Machine (CT)',
    description: 'Select your coffee machine model (CD)',
  },
};

/**
 * Second component for ProductMergeContainer - Accessories.
 * Products from this component will be merged with the first component.
 */
export const mockMergeComponent2Config: MockComponentForMergeConfig = {
  componentKey: 'accessories',
  viewElement: {
    id: 'merge-component-2',
    kind: 'component',
    type: 'default',
    name: 'Accessories',
    enabled: true,
    source: { type: 'component', componentKey: 'accessories' },
    uiControl: { showTitle: true, showDescription: true, showForTwoOrMore: false },
  } as IUICfgComponent,
  products: [
    {
      productId: 2001,
      productKey: 'acc-grinder',
      texts: {
        name: 'Coffee Grinder (PT)',
        description: 'Burr grinder with 40 grind settings for perfect extraction (PD)',
        shortDescription: '40 grind settings (PSD)',
      },
      images: [{ url: PLACEHOLDER_IMAGES.gray }],
    },
    {
      productId: 2002,
      productKey: 'acc-frother',
      texts: {
        name: 'Milk Frother (PT)',
        description: 'Automatic milk frother for lattes and cappuccinos (PD)',
        shortDescription: 'Automatic frothing (PSD)',
      },
      images: [{ url: PLACEHOLDER_IMAGES.teal }],
    },
  ],
  textData: {
    name: 'Accessories (CT)',
    description: 'Add optional accessories to your setup (CD)',
  },
};

// ============================================================================
// Full Example Container Configs
// These are specialized configurations for the StyleGuideFullExample component.
// ============================================================================

/**
 * ComponentItems container with showTitle and showDescription enabled.
 * Used in Full Example to demonstrate component + attribute grouping.
 */
// The Component Items container type does not mix in `IUiCfgContainerExpandable`, but this
// fixture supplies those fields on purpose so the style guide exercises title/expand rendering.
export const mockComponentItemsContainerFull: IUICfgComponentItemsContainer & IUiCfgContainerExpandable = {
  id: 'full-example-component-items',
  kind: 'container',
  type: UICfgContainerType.ComponentItems,
  name: 'Main Component',
  enabled: true,
  title: 'Main Component (CnT)',
  description: 'Configure your main product settings (CnD)',
  expandable: false,
  defaultExpanded: true,
  showTitle: true,
  showDescription: true,
  combineIntoGrid: false,
  componentsSettings: UICfgComponentContainerElementOptionsDefault,
  attributesSettings: UICfgAttributesContainerElementOptionsDefault,
  children: [] as unknown as [IUICfgComponent, ...IUICfgAttributeText[]],
};

/**
 * Mixed container for Full Example with all elements.
 * Contains component, product, attributes, and nested container.
 */
export const mockMixedContainerFull: IUICfgMixedContainer = {
  id: 'full-example-mixed',
  kind: 'container',
  type: UICfgContainerType.Mixed,
  name: 'Configuration Options',
  enabled: true,
  title: 'Configuration Options (CnT)',
  description: 'Customize your product with these additional options (CnD)',
  expandable: false,
  defaultExpanded: true,
  showTitle: true,
  showDescription: true,
  combineIntoGrid: false,
  componentsSettings: UICfgComponentContainerElementOptionsDefault,
  attributesSettings: UICfgAttributesContainerElementOptionsDefault,
  children: [],
};

/**
 * Nested ComponentItems container for Full Example.
 * Demonstrates multi-level nesting inside MixedContainer.
 */
// The Component Items container type does not mix in `IUiCfgContainerExpandable`, but this
// fixture supplies those fields on purpose so the style guide exercises title/expand rendering.
export const mockNestedComponentItemsFull: IUICfgComponentItemsContainer & IUiCfgContainerExpandable = {
  id: 'full-example-nested-component-items',
  kind: 'container',
  type: UICfgContainerType.ComponentItems,
  name: 'Accessory Settings',
  enabled: true,
  title: 'Accessory Settings (CnT)',
  description: 'Configure your accessory options (CnD)',
  expandable: false,
  defaultExpanded: true,
  showTitle: true,
  showDescription: true,
  combineIntoGrid: false,
  componentsSettings: UICfgComponentContainerElementOptionsDefault,
  attributesSettings: UICfgAttributesContainerElementOptionsDefault,
  children: [] as unknown as [IUICfgComponent, ...IUICfgAttributeText[]],
};

/**
 * ProductMerge container for Full Example.
 * Displays products from 2 components merged together.
 */
export const mockProductMergeContainerFull: IUICfgProductsMergeContainer = {
  id: 'full-example-product-merge',
  kind: 'container',
  type: UICfgContainerType.ProductMerge,
  name: 'Select Model',
  enabled: true,
  title: 'Select Model (CnT)',
  description: 'Choose from available product models (CnD)',
  expandable: false,
  defaultExpanded: true,
  showTitle: true,
  showDescription: true,
  combineIntoGrid: false,
  productsSettings: {
    ...UICfgProductsContainerElementOptionsDefault,
    layout: 'card',
    showImage: true,
    showTitle: true,
    showDescription: true,
  },
  children: [],
};

// ============================================================================
// Option Text / Group Variants
// Used in StyleGuideOptionText to demonstrate group title/description patterns
// and component title/description with switcher
// ============================================================================

export const mockOptionNameOnly: IUICfgMixedContainer = {
  ...mockMixedContainer,
  id: 'option-name-only',
  name: 'Option Name Only',
  title: 'Option name',
  description: '',
  showTitle: true,
  showDescription: false,
  expandable: false,
};

export const mockOptionNameDesc: IUICfgMixedContainer = {
  ...mockMixedContainer,
  id: 'option-name-desc',
  name: 'Option Name + Description',
  title: 'Option name',
  description: 'Organize your coffee cups with ease using our innovative cup storage rack.',
  showTitle: true,
  showDescription: true,
  expandable: false,
};

export const mockOptionCollapsed: IUICfgMixedContainer = {
  ...mockMixedContainer,
  id: 'option-collapsed',
  name: 'Option Collapsed',
  title: 'Option name',
  description: 'Organize your coffee cups with ease using our innovative cup storage rack.',
  showTitle: true,
  showDescription: true,
  expandable: true,
  defaultExpanded: false,
};

export const mockOptionExpanded: IUICfgMixedContainer = {
  ...mockMixedContainer,
  id: 'option-expanded',
  name: 'Option Expanded',
  title: 'Option name',
  description: 'Organize your coffee cups with ease using our innovative cup storage rack.',
  showTitle: true,
  showDescription: true,
  expandable: true,
  defaultExpanded: true,
};

export const mockComponentWithSwitch: MockComponentConfig = {
  viewElement: {
    ...mockCoffeeMachineComponent.viewElement,
    id: 'component-with-switch',
    name: 'Component With Switch',
    uiControl: { showTitle: true, showDescription: true, showSwitcher: true, showComponent: 'always' },
  },
  lookupConfig: {
    componentKey: 'component-switch',
    isOptional: true,
    isEnabled: true,
    name: 'Option name',
    description: 'Organize your coffee cups with ease using our innovative cup storage rack.',
  },
};
